import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { PhoneCall, PhoneMissed, PhoneOff } from "lucide-react";
import type { ReactNode, MouseEvent } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { telHref } from "../misc/ContactLinks";
import { appendDictated, MicButton } from "../misc/dictation";
import type { VoiceAttachment } from "../misc/VoiceRecorder";

// LDF: Anrufe nachhalten. Das Telefon meldet nicht, ob abgenommen wurde – deshalb:
// Tipp auf eine Nummer → Versuch merken → nach Rückkehr ins CRM fragen „erreicht?“ → Notiz/Aufgabe speichern.

const KEY = "ldf.pending-call";
const EVENT = "ldf:call-started";
const MAX_AGE_MS = 3 * 60 * 60 * 1000;

type PendingCall = {
  askedAt?: number;
  number: string;
  name: string;
  contactId?: number | null;
  companyId?: number | null;
  startedAt: number;
  leftAt?: number;
  returnedAt?: number;
};

const readPending = (): PendingCall | null => {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) ?? "null") as PendingCall | null;
    return value && Date.now() - value.startedAt < MAX_AGE_MS ? value : null;
  } catch {
    return null;
  }
};
const writePending = (value: PendingCall | null) => {
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
  } catch {
    // Speicher nicht verfügbar
  }
};

/** Anrufbarer Link – ersetzt <a href="tel:…"> überall, wo ein Anruf nachgehalten werden soll */
export const CallLink = ({
  number,
  name,
  contactId,
  companyId,
  className = "underline hover:no-underline",
  children,
}: {
  number: string;
  name: string;
  contactId?: number | string | null;
  companyId?: number | string | null;
  className?: string;
  children?: ReactNode;
}) => (
  <a
    href={telHref(number)}
    className={className}
    title={`${number} anrufen`}
    onClick={(e: MouseEvent) => {
      e.stopPropagation();
      writePending({
        number,
        name,
        contactId: contactId != null ? Number(contactId) : null,
        companyId: companyId != null ? Number(companyId) : null,
        startedAt: Date.now(),
      });
      window.dispatchEvent(new Event(EVENT));
    }}
  >
    {children ?? number}
  </a>
);

const minutesBetween = (from?: number, to?: number) =>
  from && to && to > from ? Math.max(1, Math.round((to - from) / 60000)) : null;

export const CallTracker = () => {
  const [pending, setPending] = useState<PendingCall | null>(null);
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [voice, setVoice] = useState<VoiceAttachment[]>([]);
  const [retryTask, setRetryTask] = useState(true);
  const [contactId, setContactId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const ask = (call: PendingCall) => {
    // Rückfrage nur einmal zeigen – nach einem Neustart der App nicht erneut (sonst Endlosschleife)
    writePending({ ...call, askedAt: Date.now() });
    setPending(call);
    setContactId(call.contactId ?? null);
    setMinutes(String(minutesBetween(call.leftAt ?? call.startedAt, call.returnedAt ?? Date.now()) ?? ""));
    setNote("");
    setVoice([]);
    setRetryTask(true);
    setOpen(true);
  };

  useEffect(() => {
    // App wurde beim Telefonieren beendet/neu geladen: offene Rückfrage beim Start
    const stored = readPending();
    if (stored?.askedAt) writePending(null);
    else if (stored?.leftAt) ask({ ...stored, returnedAt: stored.returnedAt ?? Date.now() });

    let fallback: ReturnType<typeof setTimeout> | undefined;
    const onStart = () => {
      clearTimeout(fallback);
      // Am Computer verlässt man das Fenster evtl. nicht (z. B. Anruf übers iPhone) – dann nach 20 s trotzdem fragen
      fallback = setTimeout(() => {
        const call = readPending();
        if (call && !call.leftAt && !call.askedAt) ask(call);
      }, 20000);
    };
    const onLeave = () => {
      const call = readPending();
      if (call && !call.leftAt && !call.askedAt && Date.now() - call.startedAt < 60000) writePending({ ...call, leftAt: Date.now() });
    };
    const onReturn = () => {
      const call = readPending();
      if (!call?.leftAt || call.returnedAt || call.askedAt) return;
      clearTimeout(fallback);
      const back = { ...call, returnedAt: Date.now() };
      writePending(back);
      ask(back);
    };
    const onVisibility = () => (document.hidden ? onLeave() : onReturn());

    window.addEventListener(EVENT, onStart);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onLeave);
    window.addEventListener("focus", onReturn);
    return () => {
      clearTimeout(fallback);
      window.removeEventListener(EVENT, onStart);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onLeave);
      window.removeEventListener("focus", onReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Anruf über Firmennummer: Ansprechpartner der Firma zur Auswahl
  const { data: companyContacts } = useQuery({
    queryKey: ["call-company-contacts", pending?.companyId],
    enabled: open && !pending?.contactId && !!pending?.companyId,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("contacts")
        .select("id,first_name,last_name,title")
        .eq("company_id", pending!.companyId!)
        .order("last_name");
      return (data ?? []) as { id: number; first_name: string | null; last_name: string | null; title: string | null }[];
    },
  });

  const close = () => {
    writePending(null);
    setOpen(false);
    // Fenster erst nach der Schließ-Animation entfernen; gesperrte Bedienung (pointer-events) sicher freigeben
    setTimeout(() => {
      setPending(null);
      document.body.style.pointerEvents = "";
    }, 250);
  };

  const save = async (outcome: "erreicht" | "nicht-erreicht") => {
    if (!pending) return;
    setSaving(true);
    const supabase = getSupabaseClient();
    const now = new Date().toISOString();
    const started = new Date(pending.startedAt).toISOString();
    const mins = Number(minutes) || null;
    try {
      const { error } = await supabase.from("calls").insert({
        contact_id: contactId,
        company_id: pending.companyId ?? null,
        phone: pending.number,
        started_at: started,
        outcome,
        duration_seconds: outcome === "erreicht" && mins ? mins * 60 : null,
        note: note.trim() || null,
        sales_id: identity?.id,
      });
      if (error) throw error;

      if (contactId) {
        const when = new Date(pending.startedAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
        const text =
          outcome === "erreicht"
            ? `📞 Telefonat mit ${pending.name}, ${when}${mins ? ` (ca. ${mins} Min.)` : ""}${note.trim() ? `\n\n${note.trim()}` : ""}`
            : `📵 Anrufversuch bei ${pending.name}, ${when} – nicht erreicht${note.trim() ? `\n\n${note.trim()}` : ""}`;
        await supabase
          .from("contact_notes")
          .insert({ contact_id: contactId, text, date: now, sales_id: identity?.id, attachments: voice.length ? voice : null });
        if (outcome === "erreicht") await supabase.from("contacts").update({ last_seen: now }).eq("id", contactId);
        if (outcome === "nicht-erreicht" && retryTask) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          await supabase.from("tasks").insert({
            contact_id: contactId,
            type: "anruf",
            text: `${pending.name} erneut anrufen (${pending.number})`,
            due_date: tomorrow.toISOString().slice(0, 10),
            sales_id: identity?.id,
          });
        }
      }
      notify(outcome === "erreicht" ? "Telefonat gespeichert" : "Anrufversuch gespeichert", { type: "success" });
      queryClient.invalidateQueries();
      close();
    } catch (e) {
      notify(`Speichern fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!pending) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent
        className="max-w-md"
        // Mikrofon-Freigabe oder App-Wechsel lösen „Fokus außerhalb“ aus – das Fenster darf dadurch nicht verschwinden
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" /> Gespräch mit {pending.name}?
          </DialogTitle>
          <DialogDescription>
            {pending.number} · {new Date(pending.startedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr
          </DialogDescription>
        </DialogHeader>

        {!pending.contactId && !!companyContacts?.length && (
          <label className="flex flex-col gap-1 text-sm">
            Mit wem gesprochen?
            <select
              value={contactId ?? ""}
              onChange={(e) => setContactId(e.target.value ? Number(e.target.value) : null)}
              className="h-10 rounded-md border border-input bg-background px-2"
            >
              <option value="">– nur bei der Firma vermerken –</option>
              {companyContacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                  {c.title ? ` · ${c.title}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm">
              Dauer ca.
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                className="h-9 w-16 rounded-md border border-input bg-background px-2"
              />
              Min.
            </label>
            <MicButton
              title="Kurz erzählen, was besprochen wurde"
              hints={pending.name}
              className="justify-end"
              onText={(t) => setNote((v) => appendDictated(v, t))}
              onAudio={(a) => setVoice((list) => [...list, a])}
            />
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Was wurde besprochen? (optional)"
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          />
          {contactId && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={retryTask} onChange={(e) => setRetryTask(e.target.checked)} />
              Bei „nicht erreicht“: Aufgabe „morgen erneut anrufen“ anlegen
            </label>
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <Button disabled={saving} onClick={() => save("erreicht")} className="h-11 gap-1">
            <PhoneCall className="h-4 w-4" /> Erreicht
          </Button>
          <Button disabled={saving} variant="outline" onClick={() => save("nicht-erreicht")} className="h-11 gap-1">
            <PhoneMissed className="h-4 w-4" /> Nicht erreicht
          </Button>
          <Button disabled={saving} variant="ghost" onClick={close} className="h-11 gap-1">
            <PhoneOff className="h-4 w-4" /> Nicht angerufen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
